import { ChevronLeft, ChevronRight, ImageOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { joinCss } from '@/shared/lib/css-utils';
import css from './image-lightbox.module.scss';

type ImageLightboxProps = {
  images: string[];
  // Index to open at; `null` keeps the viewer closed. Changing it (re)opens the
  // viewer starting from that image (so a clicked thumbnail opens in place).
  startIndex: number | null;
  onClose: () => void;
  // Alt/caption base — e.g. the product or ticket title.
  title?: string;
};

// Full-screen image viewer shared by the product detail modal and the support
// ticket drawer — same gallery UX (position indicator, prev/next, close,
// backdrop-to-dismiss). Owns its current index (seeded from `startIndex`),
// closes on Escape, and navigates with ←/→. Portaled to <body> so it layers
// above whatever modal/drawer opened it. Broken image URLs fall back to an
// icon instead of a broken <img>.
export const ImageLightbox = ({
  images,
  startIndex,
  onClose,
  title = 'Image',
}: ImageLightboxProps) => {
  const open = startIndex !== null && images.length > 0;
  const [current, setCurrent] = useState(startIndex ?? 0);
  const [failed, setFailed] = useState<Record<number, boolean>>({});

  // Reseed to the clicked thumbnail whenever the viewer is (re)opened — done
  // during render (not an effect) so the first paint already shows the right
  // image, with no flash of index 0.
  const [seed, setSeed] = useState(startIndex);
  if (startIndex !== seed) {
    setSeed(startIndex);
    if (startIndex !== null) setCurrent(startIndex);
  }

  const count = images.length;
  const safe = count > 0 ? ((current % count) + count) % count : 0;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') setCurrent((p) => p - 1);
      else if (e.key === 'ArrowRight') setCurrent((p) => p + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const step = (delta: number) => setCurrent((p) => p + delta);

  return createPortal(
    <div className={css.lightbox}>
      {/* Full-bleed backdrop — clicking outside the image closes the viewer. */}
      <button
        aria-label="Close image"
        className={css.backdrop}
        onClick={onClose}
        type="button"
      />
      <div className={css.stage}>
        <span className={css.count}>
          {safe + 1} / {count}
        </span>
        {failed[safe] ? (
          <div className={css.broken}>
            <ImageOff size={40} />
          </div>
        ) : (
          <img
            alt={`${title} ${safe + 1}`}
            className={css.img}
            onError={() => setFailed((f) => ({ ...f, [safe]: true }))}
            src={images[safe]}
          />
        )}
        {count > 1 && (
          <>
            <button
              aria-label="Previous image"
              className={joinCss(css.navBtn, css.navPrev)}
              onClick={() => step(-1)}
              type="button"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              aria-label="Next image"
              className={joinCss(css.navBtn, css.navNext)}
              onClick={() => step(1)}
              type="button"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
        <button
          aria-label="Close image"
          className={joinCss(css.navBtn, css.navClose)}
          onClick={onClose}
          type="button"
        >
          <X size={24} />
        </button>
      </div>
    </div>,
    document.body
  );
};
